import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { ClipboardCheck, ChevronRight, Zap, Flame, Droplets, Shield, Search, X, Plus, Edit2, Copy, Trash2, History } from 'lucide-react'
import { checklistApi } from '../services/checklistApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import type { ChecklistTemplate, ChecklistTemplateVersion, ChecklistType, ChecklistResponseType } from '../types'

interface EditorItem {
  section: string
  question: string
  responseType: ChecklistResponseType
}

interface EditorState {
  name: string
  type: ChecklistType
  systemType: string
  description: string
  items: EditorItem[]
}

const EMPTY_EDITOR: EditorState = { name: '', type: 'PFC', systemType: '', description: '', items: [{ section: '', question: '', responseType: 'PassFail' }] }

interface EquipmentGroup {
  name: string
  pfc: ChecklistTemplate | undefined
  fpt: ChecklistTemplate | undefined
}

interface DisciplineGroup {
  name: string
  icon: React.ReactNode
  equipment: EquipmentGroup[]
}

const DISCIPLINE_ORDER = ['Electrical', 'Mechanical', 'Plumbing', 'Cybersecurity']
const DISCIPLINE_ICONS: Record<string, React.ReactNode> = {
  Electrical: <Zap size={16} />,
  Mechanical: <Flame size={16} />,
  Plumbing: <Droplets size={16} />,
  Cybersecurity: <Shield size={16} />,
}
const DISCIPLINE_COLORS: Record<string, string> = {
  Electrical: 'var(--accent-yellow, #f59e0b)',
  Mechanical: 'var(--accent-red, #ef4444)',
  Plumbing: 'var(--accent-primary, #3b82f6)',
  Cybersecurity: 'var(--accent-purple, #a855f7)',
}

export function ChecklistTemplates() {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [collapsedDisciplines, setCollapsedDisciplines] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [viewingTemplate, setViewingTemplate] = useState<ChecklistTemplate | null>(null)
  const [versions, setVersions] = useState<ChecklistTemplateVersion[]>([])
  const [showEditor, setShowEditor] = useState(false)
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [copying, setCopying] = useState(false)

  // Load version history when the viewer opens
  useEffect(() => {
    setVersions([])
    if (viewingTemplate?.parentTemplateId) {
      checklistApi.getTemplateVersions(String(viewingTemplate.id))
        .then(setVersions)
        .catch(() => setVersions([]))
    }
  }, [viewingTemplate?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function openCreate() {
    setEditingTemplateId(null)
    setEditor(EMPTY_EDITOR)
    setEditorError(null)
    setShowEditor(true)
  }

  function openEdit(t: ChecklistTemplate) {
    setEditingTemplateId(String(t.id))
    setEditor({
      name: t.name,
      type: t.type,
      systemType: t.systemType || '',
      description: t.description || '',
      items: t.items.length > 0
        ? t.items.map(i => ({ section: i.section || '', question: i.question, responseType: i.responseType }))
        : [{ section: '', question: '', responseType: 'PassFail' }],
    })
    setEditorError(null)
    setViewingTemplate(null)
    setShowEditor(true)
  }

  async function handleCopy(t: ChecklistTemplate) {
    try {
      setCopying(true)
      const copy = await checklistApi.copyTemplate(String(t.id))
      setViewingTemplate(null)
      await loadTemplates()
      openEdit(copy)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy template')
    } finally {
      setCopying(false)
    }
  }

  async function viewVersion(versionId: number) {
    try {
      const t = await checklistApi.getTemplate(String(versionId))
      setViewingTemplate(t)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load version')
    }
  }

  async function saveTemplate() {
    if (!editor.name.trim()) { setEditorError('Name is required.'); return }
    const items = editor.items.filter(i => i.question.trim())
    if (items.length === 0) { setEditorError('At least one item with a question is required.'); return }
    try {
      setSavingTemplate(true)
      setEditorError(null)
      const payload = {
        name: editor.name.trim(),
        type: editor.type,
        systemType: editor.systemType.trim(),
        description: editor.description.trim(),
        items: items.map((i, idx) => ({
          section: i.section.trim(),
          question: i.question.trim(),
          responseType: i.responseType,
          options: [],
          sortOrder: idx + 1,
          required: true,
        })),
      }
      if (editingTemplateId) {
        await checklistApi.updateTemplate(editingTemplateId, payload)
      } else {
        await checklistApi.createTemplate(payload)
      }
      setShowEditor(false)
      await loadTemplates()
    } catch (err) {
      setEditorError(err instanceof Error ? err.message : 'Failed to save template')
    } finally {
      setSavingTemplate(false)
    }
  }

  function updateItem(idx: number, updates: Partial<EditorItem>) {
    setEditor(prev => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, ...updates } : it) }))
  }

  useEffect(() => { loadTemplates() }, [])

  const disciplines = useMemo((): DisciplineGroup[] => {
    // Group templates by equipment name, pairing PFC + FPT
    const equipMap = new Map<string, { pfc?: ChecklistTemplate; fpt?: ChecklistTemplate; discipline: string }>()
    for (const t of templates) {
      const key = t.name
      const discipline = t.systemType || 'General'
      if (!equipMap.has(key)) {
        equipMap.set(key, { discipline })
      }
      const entry = equipMap.get(key)!
      if (t.type === 'PFC') entry.pfc = t
      else if (t.type === 'FPT') entry.fpt = t
    }

    // Group by discipline
    const discMap = new Map<string, EquipmentGroup[]>()
    for (const [name, entry] of equipMap) {
      const disc = entry.discipline
      if (!discMap.has(disc)) discMap.set(disc, [])
      discMap.get(disc)!.push({ name, pfc: entry.pfc, fpt: entry.fpt })
    }

    // Sort equipment within each discipline
    for (const equips of discMap.values()) {
      equips.sort((a, b) => a.name.localeCompare(b.name))
    }

    // Sort disciplines in order
    const result: DisciplineGroup[] = []
    for (const disc of DISCIPLINE_ORDER) {
      if (discMap.has(disc)) {
        result.push({
          name: disc,
          icon: DISCIPLINE_ICONS[disc] || <ClipboardCheck size={16} />,
          equipment: discMap.get(disc)!,
        })
        discMap.delete(disc)
      }
    }
    // Any remaining disciplines
    for (const [disc, equips] of discMap) {
      result.push({
        name: disc,
        icon: <ClipboardCheck size={16} />,
        equipment: equips,
      })
    }
    return result
  }, [templates])

  const filteredDisciplines = useMemo((): DisciplineGroup[] => {
    if (!searchQuery.trim() && !typeFilter) return disciplines
    const q = searchQuery.toLowerCase()
    return disciplines
      .map(d => ({
        ...d,
        equipment: d.equipment.filter(e => {
          if (typeFilter === 'PFC' && !e.pfc) return false
          if (typeFilter === 'FPT' && !e.fpt) return false
          if (q && !e.name.toLowerCase().includes(q)) return false
          return true
        }),
      }))
      .filter(d => d.equipment.length > 0)
  }, [disciplines, searchQuery, typeFilter])

  const totalEquipment = useMemo(() => disciplines.reduce((sum, d) => sum + d.equipment.length, 0), [disciplines])

  async function loadTemplates() {
    try {
      setLoading(true)
      const data = await checklistApi.getTemplates()
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  function toggleDiscipline(name: string) {
    setCollapsedDisciplines(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  if (loading) return <LoadingSpinner message="Loading templates..." />

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Checklist Templates</h1>
          <p className="page-subtitle">{totalEquipment} equipment types &middot; {templates.length} templates</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> New Template</button>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {templates.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={48} />}
          title="No templates"
          message="No checklist templates have been loaded yet."
        />
      ) : (
        <>
          {/* Search + type filter */}
          <div className="d-flex gap-2 align-center" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 360 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                className="form-input"
                placeholder="Search equipment types..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: 34 }}
              />
            </div>
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 120 }}
            >
              <option value="">All Types</option>
              <option value="PFC">Has PFC</option>
              <option value="FPT">Has FPT</option>
            </select>
            {(searchQuery || typeFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearchQuery(''); setTypeFilter('') }}>
                <X size={14} /> Clear
              </button>
            )}
          </div>

          {/* Discipline sections */}
          {filteredDisciplines.map(discipline => {
            const isCollapsed = collapsedDisciplines.has(discipline.name)
            const accentColor = DISCIPLINE_COLORS[discipline.name] || 'var(--accent-primary)'

            return (
              <div key={discipline.name} style={{ marginBottom: 16 }}>
                {/* Discipline header */}
                <button
                  onClick={() => toggleDiscipline(discipline.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '10px 14px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: isCollapsed ? 'var(--radius-md)' : 'var(--radius-md) var(--radius-md) 0 0',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  <ChevronRight
                    size={16}
                    style={{
                      transition: 'transform 0.15s',
                      transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                      color: 'var(--text-muted)',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: accentColor, display: 'flex', alignItems: 'center' }}>{discipline.icon}</span>
                  <span>{discipline.name}</span>
                  <span style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    fontWeight: 400,
                    marginLeft: 4,
                  }}>
                    {discipline.equipment.length} type{discipline.equipment.length !== 1 ? 's' : ''}
                  </span>
                </button>

                {/* Equipment table */}
                {!isCollapsed && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderTop: 'none',
                    borderRadius: '0 0 var(--radius-md) var(--radius-md)',
                    overflow: 'hidden',
                  }}>
                    {/* Table header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 110px 110px',
                      padding: '6px 14px',
                      background: 'var(--bg-tertiary)',
                      borderBottom: '1px solid var(--border-color)',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>
                      <span>Equipment Type</span>
                      <span style={{ textAlign: 'center' }}>PFC</span>
                      <span style={{ textAlign: 'center' }}>FPT</span>
                    </div>

                    {/* Equipment rows */}
                    {discipline.equipment.map((equip, idx) => (
                      <div
                        key={equip.name}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 110px 110px',
                          padding: '8px 14px',
                          borderBottom: idx < discipline.equipment.length - 1 ? '1px solid var(--border-color)' : 'none',
                          alignItems: 'center',
                          fontSize: '13px',
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{equip.name}</span>

                        {/* PFC badge */}
                        <div style={{ textAlign: 'center' }}>
                          {equip.pfc ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={() => setViewingTemplate(equip.pfc!)}
                              style={{ fontSize: '12px', padding: '2px 10px' }}
                            >
                              <span style={{ color: 'var(--accent-primary)' }}>PFC</span>
                              <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{equip.pfc.items.length} items</span>
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
                          )}
                        </div>

                        {/* FPT badge */}
                        <div style={{ textAlign: 'center' }}>
                          {equip.fpt ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={() => setViewingTemplate(equip.fpt!)}
                              style={{ fontSize: '12px', padding: '2px 10px' }}
                            >
                              <span style={{ color: 'var(--accent-green, #22c55e)' }}>FPT</span>
                              <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{equip.fpt.items.length} items</span>
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
                          )}
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}

      {/* Read-only template viewer */}
      {viewingTemplate && createPortal(
        <div className="modal-overlay" onClick={() => setViewingTemplate(null)}>
          <div className="modal modal-scrollable" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800, width: '95vw' }}>
            <div className="modal-header">
              <div>
                <h2>{viewingTemplate.name}</h2>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {viewingTemplate.type} &middot; {viewingTemplate.systemType} &middot; {viewingTemplate.items.length} items
                  &middot; v{viewingTemplate.version}
                  {!viewingTemplate.isCurrent && <span style={{ color: 'var(--accent-yellow, #f59e0b)', marginLeft: 6 }}>(archived version)</span>}
                </p>
              </div>
              <button className="modal-close" onClick={() => setViewingTemplate(null)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              {(() => {
                const sections: { section: string; items: typeof viewingTemplate.items }[] = []
                let currentSection = ''
                for (const item of viewingTemplate.items) {
                  if (item.section !== currentSection) {
                    currentSection = item.section
                    sections.push({ section: currentSection, items: [] })
                  }
                  sections[sections.length - 1]!.items.push(item)
                }
                return sections.map((group) => (
                  <div key={group.section}>
                    <div style={{
                      padding: '6px 14px',
                      background: 'var(--bg-tertiary)',
                      borderBottom: '1px solid var(--border-color)',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--accent-primary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {group.section}
                    </div>
                    {group.items.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '36px 1fr 70px',
                          padding: '7px 14px',
                          borderBottom: '1px solid var(--border-color)',
                          fontSize: '13px',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{item.sortOrder}</span>
                        <span>{item.question}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'right' }}>
                          {item.responseType === 'PassFail' ? 'P/F' : item.responseType === 'YesNo' ? 'Y/N' : item.responseType}
                        </span>
                      </div>
                    ))}
                  </div>
                ))
              })()}
            </div>
            {versions.length > 1 && (
              <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <History size={12} /> Version History
                </div>
                {versions.map(v => (
                  <div key={v.id} className="d-flex align-center gap-2" style={{ fontSize: '12px', padding: '3px 0' }}>
                    <span style={{ fontWeight: 600, minWidth: 28 }}>v{v.version}</span>
                    <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{v.name} &middot; {v.itemCount} items &middot; {new Date(v.createdAt).toLocaleDateString()}</span>
                    {v.id === Number(viewingTemplate.id)
                      ? <span className="badge badge-primary-light">viewing</span>
                      : <button className="btn btn-ghost btn-xs" onClick={() => viewVersion(v.id)}>View</button>}
                  </div>
                ))}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => handleCopy(viewingTemplate)} disabled={copying}>
                <Copy size={14} /> {copying ? 'Copying...' : 'Copy'}
              </button>
              {viewingTemplate.isCurrent && (
                <button className="btn btn-primary" onClick={() => openEdit(viewingTemplate)}>
                  <Edit2 size={14} /> Edit
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setViewingTemplate(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Template editor (create / new version) */}
      {showEditor && createPortal(
        <div className="modal-overlay" onClick={() => !savingTemplate && setShowEditor(false)}>
          <div className="modal modal-scrollable" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860, width: '95vw' }}>
            <div className="modal-header">
              <div>
                <h2>{editingTemplateId ? 'Edit Template' : 'New Template'}</h2>
                {editingTemplateId && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Saving creates a new version; existing checklists keep the version they were created from.
                  </p>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowEditor(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {editorError && (
                <div className="info-card danger mb-4"><p style={{ margin: 0 }}>{editorError}</p></div>
              )}
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={editor.name} onChange={e => setEditor(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={editor.type} onChange={e => setEditor(p => ({ ...p, type: e.target.value as ChecklistType }))}>
                    <option value="PFC">PFC</option>
                    <option value="FPT">FPT</option>
                    <option value="BEIC">BEIC</option>
                    <option value="Integrated">Integrated</option>
                    <option value="Seasonal">Seasonal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Discipline / Category</label>
                  <input className="form-input" placeholder="e.g. Mechanical" value={editor.systemType} onChange={e => setEditor(p => ({ ...p, systemType: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={2} value={editor.description} onChange={e => setEditor(p => ({ ...p, description: e.target.value }))} />
              </div>

              <div className="d-flex justify-between align-center" style={{ marginTop: 8, marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Items ({editor.items.length})</label>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditor(p => ({ ...p, items: [...p.items, { section: p.items[p.items.length - 1]?.section || '', question: '', responseType: 'PassFail' }] }))}>
                  <Plus size={14} /> Add Item
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '24px 180px 1fr 110px 32px', gap: 6, fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 2px', marginBottom: 4 }}>
                <span>#</span><span>Section</span><span>Question</span><span>Response</span><span></span>
              </div>
              {editor.items.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '24px 180px 1fr 110px 32px', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{idx + 1}</span>
                  <input className="form-input" style={{ fontSize: '13px' }} value={item.section} onChange={e => updateItem(idx, { section: e.target.value })} />
                  <input className="form-input" style={{ fontSize: '13px' }} value={item.question} onChange={e => updateItem(idx, { question: e.target.value })} />
                  <select className="form-select" style={{ fontSize: '13px' }} value={item.responseType} onChange={e => updateItem(idx, { responseType: e.target.value as ChecklistResponseType })}>
                    <option value="PassFail">Pass/Fail</option>
                    <option value="YesNo">Yes/No</option>
                    <option value="Text">Text</option>
                    <option value="Number">Number</option>
                  </select>
                  <button className="btn btn-ghost btn-xs btn-icon text-danger" title="Remove item" onClick={() => setEditor(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditor(false)} disabled={savingTemplate}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTemplate} disabled={savingTemplate}>
                {savingTemplate ? 'Saving...' : editingTemplateId ? 'Save New Version' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  )
}
